import { useMemo } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/shared/components/ui/carousel';
import { AccountCard } from '@/widgets/account-card';
import InvestAccountIcon from '@/shared/assets/account-invest-icon.svg?react';
import SpotAccountIcon from '@/shared/assets/account-spot-icon.svg?react';
import MarginAccountIcon from '@/shared/assets/account-margin-icon.svg?react';
import EarnAccountIcon from '@/shared/assets/account-earn-icon.svg?react';
import { useTranslation } from 'react-i18next';
import LogoSmallIcon from '@/shared/assets/f2ex-logo-small.svg?react';
import AccountsCarouselControlButton from './AccountsCarouselControlButton';
import { AccountCardNotOpened } from './AccountCardNotOpened';
import { AccountCardContentSkeleton } from './AccountCardContentSkeleton';
import type { AccountSectionCardType } from '@/shared/types';
import {
  usePendingAccountsStore,
  useOpenAccountAvailabilityStore,
} from '@/features/account-open';
import {
  useAccountsStore,
  type AccountsRestsByType,
  type AccountType,
} from '@/entities/account';
import { useAppSettingsStore } from '@/shared/lib';

type Props = {
  isBalancesHidden: boolean;
  isLoading: boolean;
  onDeposit: () => void;
  onWithdraw: () => void;
  onInternalTransfer: (context?: {
    accountType: AccountType;
    accountNumber: string;
    assetCode: string;
    direction?: 'from' | 'to';
  }) => void;
  accountsRests: AccountsRestsByType;
  onAccountClick: (accountNumber: string) => void;
  onExchange: () => void;
  visibleTypes: Array<AccountType>;
};

const AccountsCarousel = ({
  isBalancesHidden,
  isLoading,
  onDeposit,
  onWithdraw,
  onInternalTransfer,
  accountsRests,
  onAccountClick,
  onExchange,
  visibleTypes,
}: Props) => {
  const { t } = useTranslation();
  const { ewallet, invest, spot, leverage, earn } = useAccountsStore();
  const { pendingAccounts } = usePendingAccountsStore();
  const {
    spotCreateIsActive,
    investCreateIsActive,
    marginCreateIsActive,
    earnCreateIsActive,
  } = useOpenAccountAvailabilityStore();
  const { baseAsset } = useAppSettingsStore();

  const createIsActiveByType: Partial<Record<AccountType, boolean>> = {
    spot: spotCreateIsActive,
    invest: investCreateIsActive,
    leverage: marginCreateIsActive,
    earn: earnCreateIsActive,
  };

  const accountsCardsByType: Array<AccountSectionCardType> = useMemo(() => {
    const cards: Array<AccountSectionCardType> = [
      {
        type: 'ewallet',
        title: t('personalAccount.overview.cards.ewallet'),
        icon: LogoSmallIcon,
        label: LogoSmallIcon,
        data: ewallet,
        isPending: false,
        exists: !!ewallet && ewallet.accounts.length > 0,
        created: ewallet?.created || '',
      },
      {
        type: 'invest',
        title: t('personalAccount.overview.cards.invest'),
        icon: InvestAccountIcon,
        label: LogoSmallIcon,
        data: invest,
        isPending: pendingAccounts.some(
          account => account.accountType === 'invest'
        ),
        exists: !!invest && invest.accounts.length > 0,
        created: invest?.created || '',
      },
      {
        type: 'spot',
        title: t('personalAccount.overview.cards.spot'),
        icon: SpotAccountIcon,
        label: LogoSmallIcon,
        data: spot,
        isPending: pendingAccounts.some(
          account => account.accountType === 'spot'
        ),
        exists: !!spot && spot.accounts.length > 0,
        created: spot?.created || '',
      },
      {
        type: 'leverage',
        title: t('personalAccount.overview.cards.leverage'),
        icon: MarginAccountIcon,
        label: LogoSmallIcon,
        data: leverage,
        isPending: pendingAccounts.some(
          account => account.accountType === 'margin'
        ),
        exists: !!leverage && leverage.accounts.length > 0,
        created: leverage?.created || '',
      },
      {
        type: 'earn',
        title: t('personalAccount.overview.cards.earn'),
        icon: EarnAccountIcon,
        label: LogoSmallIcon,
        data: earn,
        isPending: pendingAccounts.some(
          account => account.accountType === 'earn'
        ),
        exists: !!earn && earn.accounts.length > 0,
        created: earn?.created || '',
      },
    ];

    return cards
      .filter(card => visibleTypes.includes(card.type))
      .sort(
        (a, b) => visibleTypes.indexOf(a.type) - visibleTypes.indexOf(b.type)
      );
  }, [t, ewallet, invest, spot, leverage, earn, pendingAccounts, visibleTypes]);

  return (
    <Carousel
      opts={{
        align: 'start',
      }}
      className="w-full">
      <CarouselContent
        wrapperClassName="overflow-visible"
        className="-ml-0 gap-[2.4rem]">
        {isLoading ? (
          <AccountCardContentSkeleton />
        ) : (
          accountsCardsByType.map(accountCard => {
            const isPending = pendingAccounts.some(
              account =>
                account.accountType === accountCard.type ||
                (account.accountType === 'margin' &&
                  accountCard.type === 'leverage')
            );
            const isVisible = accountCard.exists || isPending;

            if (!isVisible) {
              if (!createIsActiveByType[accountCard.type]) {
                return null;
              }

              return (
                <CarouselItem
                  key={accountCard.type}
                  className="basis-[22rem] pl-0">
                  <AccountCardNotOpened
                    type={accountCard.type}
                    accountTitle={accountCard.title}
                    icon={accountCard.icon}
                  />
                </CarouselItem>
              );
            }

            const { balance, deltaPercent } = accountCard.data || {
              balance: null,
              deltaPercent: null,
            };

            return (
              <CarouselItem
                key={accountCard.type}
                className="basis-[22rem] pl-0">
                <AccountCard
                  isBalancesHidden={isBalancesHidden}
                  type={accountCard.type}
                  accountTitle={accountCard.title}
                  balance={balance}
                  deltaPercent={deltaPercent}
                  isPending={isPending}
                  currency={baseAsset}
                  icon={accountCard.icon}
                  onDeposit={onDeposit}
                  onWithdraw={onWithdraw}
                  onInternalTransfer={onInternalTransfer}
                  onExchange={onExchange}
                  onAccountClick={onAccountClick}
                  accounts={
                    accountCard.type === 'leverage' ||
                    accountCard.type === 'invest' ||
                    accountCard.type === 'earn'
                      ? (accountsRests?.[accountCard.type]?.accounts ?? [])
                      : (accountsRests?.[accountCard.type]?.flatMap(
                          account => account.accounts
                        ) ?? [])
                  }
                />
              </CarouselItem>
            );
          })
        )}
        <CarouselItem className="basis-[2rem] pl-0" />
      </CarouselContent>

      <AccountsCarouselControlButton type="previous" />
      <AccountsCarouselControlButton type="next" />
    </Carousel>
  );
};

export { AccountsCarousel };
